import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from '../../api';
import { LinearProgress, Stack, Alert } from "@mui/material";
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import { connect, useDispatch } from "react-redux";
import { updateUser, updateImageCaption } from "../../redux/reducers/userReducer";
import SimpleImageSlider from "react-simple-image-slider";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";

interface SectionAttachImagesProps {
  entity: string;
  entity_id: any;
  attachmentKey: string;
  multiple: boolean;
  imgSet: any[];
  onSaveCallback?: (hasChanges: boolean, saveFunction?: () => void) => void;
  onImageClick?: () => void;
}

const PageAttachImages = ({user, entity, entity_id, attachmentKey, multiple, imgSet, onSaveCallback, onImageClick}: SectionAttachImagesProps & {user: any}) => {
  const dispatch = useDispatch();
  
  const [images, setImages] = useState([]);
  const [imagesUploading, setImagesUploading] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentIndex, setIndex] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [sbState, setSBState] = useState({
    open: false,
  });
  const { open } = sbState;

  const fetchSavedCaption = useCallback(async (userId: number) => {
    try {
      const requestData = { 
        id: userId,
        entity: 'users'
      };
      
      api.updateAttachment(requestData, (response) => {
        if (response && response.caption) {
          dispatch(updateUser({
            avatar_caption: response.caption
          }));
          
          setImages(prevImages => 
            prevImages.map(img => 
              img.id === userId ? { ...img, caption: response.caption } : img
            )
          );
          
          setCurrentImage(prevImage => 
            prevImage && prevImage.id === userId 
              ? { ...prevImage, caption: response.caption }
              : prevImage
          );
        }
      });
    } catch (error) {
      console.error('Error fetching saved caption:', error);
    }
  }, [dispatch]);
  
  const processImages = useCallback(() => {
    let processedImages = [];
    
    if (entity === 'users') {
      if (user?.avatar) {
        processedImages = [{
          id: user.id,
          isLoading: false,
          name: user.avatar,
          caption: user.avatar_caption || '',
          url: `${api.getUploadUrl()}/${user.avatar}`,
        }];
      } else if (imgSet && imgSet.length > 0) {
        processedImages = imgSet.map((i) => ({
          id: i.id,
          isLoading: false,
          name: i.url,
          caption: i.caption || '',
          url: `${api.getUploadUrl()}/${i.url}`,
        }));
      }
    } else if (imgSet && imgSet.length > 0) {
      processedImages = imgSet.map((i) => ({
        id: i.id,
        isLoading: false,
        name: i.url,
        caption: i.caption || '',
        url: `${api.getUploadUrl()}/${i.url}`,
      }));
    }
    
    setImages(processedImages);
  }, [user?.avatar, user?.avatar_caption, user?.id, imgSet, entity]);

  useEffect(() => {
    if (entity === 'users' && user?.avatar && !user?.avatar_caption && user?.id) {
      fetchSavedCaption(user.id);
    }
  }, [entity, user?.avatar, user?.avatar_caption, user?.id, fetchSavedCaption]);

  const saveChanges = useCallback(() => {
    if (!pendingChanges || !currentImage) return;
    setLoading(true);
    
    const updateData = {
      id: currentImage.id,
      caption: currentImage.caption,
      ...(entity === 'users' && { entity: 'users' })
    };
    
    api.updateAttachment(updateData, (res) => {
      setLoading(false);
      setPendingChanges(false);
      setHasUnsavedChanges(false);
      
      if (entity === 'users') {
        dispatch(updateUser({
          avatar_caption: currentImage.caption
        }));
      } else {
        dispatch(updateImageCaption({
          index: currentIndex,
          caption: currentImage.caption
        }));
      }
      openSuccessSnackbar({ vertical: 'bottom', horizontal: 'center' });
    });
  }, [pendingChanges, currentImage, entity, currentIndex, dispatch]);

  const openSuccessSnackbar = (position: SnackbarOrigin) => {
    setSBState({ ...position, open: true });
  };

  const handleCloseSB = () => {
    setSBState({ ...sbState, open: false });
  };

  useEffect(() => {
    processImages();
  }, [processImages]);

  useEffect(() => {
    const newCurrentImage = images && images.length ? images[currentIndex] : null;
    setCurrentImage(newCurrentImage);
  }, [images, currentIndex]);

  useEffect(() => {
    if (onSaveCallback) {
      onSaveCallback(hasUnsavedChanges, saveChanges);
    }
  }, [hasUnsavedChanges, onSaveCallback, saveChanges]);

  const isFileExists = (f) => {
    for(let i in images) {
      if(images[i].name === f.name) return true;
    }
    return false;
  }

  const handleOnChange = (e) => {
    let files = e.target.files;
    if (!files || files.length === 0) return;

    let uploadsInProgress = [...imagesUploading];

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      if (!file || !file.name) continue;
      
      if (entity !== 'users' && isFileExists(file)) continue;

      const uploadingFile = {
        id: `temp-${Date.now()}-${i}`,
        isLoading: true,
        name: file.name,
        url: '',
      };

      uploadsInProgress.push(uploadingFile);
      setImagesUploading(uploadsInProgress);

      let formData = new FormData();
      formData.append("file", file);
      formData.append("key", attachmentKey);
      formData.append("entity", entity);
      formData.append("entity_id", entity_id);

      api.uploadAttachment(formData, (res) => {
        const attachment = { ...res.data };
        setImagesUploading(prev => prev.filter(item => item.name !== file.name));
        handleUploadSuccess(attachment);
      });
    }
  }

  const handleUploadSuccess = (file) => {
    const avatarField = file.avatar || file.url || file.file_path || file.filename;
    
    if (entity === 'users') {
      const userUpdate = {
        avatar: avatarField,
        avatar_caption: file.caption || file.avatar_caption || ''
      };
      
      dispatch(updateUser(userUpdate));
    }
    const newImage = {
      id: file.id || user?.id || Date.now(),
      isLoading: false,
      name: avatarField,
      caption: file.caption || file.avatar_caption || '',
      url: `${api.getUploadUrl()}/${avatarField}`,
    };
    
    if (entity === 'users') {
      setImages([newImage]);
      setCurrentImage(newImage);
      setIndex(0);
    } else {
      setImages(prev => [...prev, newImage]);
    }
    
    setHasUnsavedChanges(true);
  }

  const handleArrowClick = (e) => {
    const max = images.length - 1;
    let i = 0;
    if (e) {
      i = currentIndex !== max ? (currentIndex + 1) : 0;
    } else {
      i = currentIndex !== 0 ? (currentIndex - 1) : max;
    }
    
    setIndex(i);
  }

  const handleBulletClick = (i) => {
    setIndex(i);
  }

  const handleCaptionChange = (e) => {
    const newCaption = e.target.value;
    
    setCurrentImage(prev => prev ? { ...prev, caption: newCaption } : null);
    
    setImages(prevImages =>
      prevImages.map((image, index) =>
        index === currentIndex ? { ...image, caption: newCaption } : image
      )
    );

    setPendingChanges(true);
    setHasUnsavedChanges(true);
  };

  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick();
    }
  };

  return (
    <>
      <div className="space-y-8">
        <div>
          <div className="mt-5">
            {images.length > 0 ? (
              <div className="relative">
                {images.length === 1 ? (
                  <div className="w-full h-[300px] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src={images[0].url}
                      alt="Profile"
                      className={`w-full h-full object-cover ${onImageClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                      onClick={handleImageClick}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div 
                    className={onImageClick ? 'cursor-pointer' : ''}
                    onClick={handleImageClick}
                  >
                    <SimpleImageSlider
                      key={`slider-${images[0]?.url}-${images.length}`}
                      style={{width:'100% !important'}}
                      width={'100%'}
                      height={300}
                      images={images}
                      showBullets={!isLoading && images.length > 1}
                      showNavs={!isLoading && images.length > 1}
                      onClickNav={handleArrowClick}
                      onClickBullets={handleBulletClick}
                    />
                  </div>
                )}
                {currentImage && (
                  <>
                    <Textarea 
                      className="mt-6"
                      placeholder="Tell us what this photo is about"  
                      rows={2}
                      name='caption'
                      value={currentImage?.caption ?? ""}
                      onChange={handleCaptionChange}
                    />
                    {isLoading && <LinearProgress />}
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-[300px] bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <span className="text-gray-500">No images uploaded yet</span>
              </div>
            )}
            
            <Stack 
              direction={'row'} 
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {imagesUploading && imagesUploading.length > 0 && imagesUploading.map((r, i) => {
                if(r.isLoading) {
                  return (
                    <div key={i} className="image-item-box space-y-1 text-center">
                      <div>{r.name}</div>
                      <LinearProgress sx={{ bottom: '0' }} />
                    </div>
                  );
                }
                return null;
              })}
            </Stack>
          </div>
          
          <div className="mt-5">
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-neutral-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
                <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                  <label
                    htmlFor={`file-upload-${attachmentKey}`}
                    className="relative cursor-pointer rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                  >
                    <span>Upload files</span>
                    <input
                      id={`file-upload-${attachmentKey}`}
                      name="files"
                      type="file"
                      multiple={multiple && entity !== 'users'}
                      accept="image/*"
                      className="sr-only"
                      onChange={handleOnChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </div>
          </div>
          
          {pendingChanges && entity !== 'users' && (
            <div className="mt-6 flex justify-end">
              <ButtonPrimary 
                onClick={saveChanges} 
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save Caption"}
              </ButtonPrimary>
            </div>
          )}
        </div>
      </div>
      
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseSB}
      >
        <Alert onClose={handleCloseSB} severity="success" variant="filled" sx={{ width: "100%" }}>
          Image Caption Saved Successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

export default connect(mapStateToProps, { updateUser, updateImageCaption })(
  PageAttachImages
);